import type { Row } from "@rocicorp/zero";
import { styled } from "goober";
import { type ComponentProps, type ReactNode, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import { useAdminLandingView } from "../hooks/useAdminLandingView";
import { useAdminSpaceView } from "../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../hooks/useAdminViewSubject";
import { navEnterStyles, useNavTransition } from "../hooks/useNavTransition";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePendingRequestCount } from "../hooks/usePendingRequestCount";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { useQuery } from "@rocicorp/zero/react";
import { useRecordNavigationClick } from "../hooks/useRecordNavigationClick";
import { useScrollFade } from "../hooks/useScrollFade";
import { useDiscoverableSpacesCollapsed } from "../hooks/useDiscoverableSpacesCollapsed";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { isSpaceMember } from "../hooks/useSpaces";
import { spaceIcon, spaceIconTooltip } from "../lib/spaceIcon";
import { useMakeUrl, routes, useRouteMatch } from "../lib/urls";
import { BadgeDot } from "./BadgeDot";
import { AdminNavigation } from "./AdminNavigation";
import { ExpandWrapper } from "./Chat/Expand";
import { DiscoverableSpaceIcon } from "./DiscoverableSpaceIcon";
import { Icon } from "./Icon";
import { JoinSpaceModal } from "./JoinSpaceModal";
import { Link } from "./Link";
import { PressButton } from "./PressButton";
import { SpaceActionsMenu } from "./SpaceActionsMenu";
import { SubtleButton, SubtleButtonLink } from "./SubtleButton";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

export const Container = styled("div")`
  width: 100%;
  padding: 12px 12px 0;
  overflow: auto;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  ${navEnterStyles}
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px),
    black calc(100% - var(--fade-bottom, 0px)),
    transparent
  );
  mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px),
    black calc(100% - var(--fade-bottom, 0px)),
    transparent
  );
`;

const linkStyles = `
  --item-bg: var(--surface-primary);
  --item-active-bg: linear-gradient(var(--alpha4)), var(--surface-primary);
  padding: 8px;
  display: flex;
  border-radius: 8px;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  color: var(--text-primary);
  gap: 8px;
  &:hover {
    background-color: var(--alpha4) !important;
    --item-bg: var(--item-active-bg);
  }
`;

export const StyledLink = styled(Link)`
  ${linkStyles}
`;

const DisabledPersonalItem = styled("span")`
  ${linkStyles}
  flex: 1;
  cursor: not-allowed;
  > * {
    opacity: 0.5;
  }
`;

export const Heading = styled("h4")`
  padding: 10px 8px 10px 8px;
  padding-right: 4px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Break = styled("hr")`
  border: none;
  margin: 8px;
  height: 0.5px;
  background-color: var(--border-alpha);
`;

// Deliberately no `content-visibility`: it zeroes the inner's measured height the instant we collapse, so the ExpandWrapper `0fr`↔`1fr` grid transition would snap shut instead of animating.
const CollapseInner = styled("div")`
  overflow: hidden;
  min-height: 0;
`;

export const SpaceItemContainer = styled("div")`
  position: relative;
  display: flex;
  align-items: center;

  & [data-space-action] {
    opacity: 0;
    pointer-events: none;
  }

  &:hover [data-space-action] {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover [data-hide-on-hover] {
    opacity: 0;
  }
`;

function SpaceAction({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();

  return (
    <div
      data-space-action
      style={{
        position: "absolute",
        right: 4,
        opacity: isMobile ? 1 : undefined,
        pointerEvents: isMobile ? "auto" : undefined,
      }}
    >
      {children}
    </div>
  );
}

const SidebarButtonElement = styled(PressButton)`
  ${linkStyles}
  width: 100%;
  border: none;
  background: transparent;
  font: inherit;
  cursor: pointer;
  &[data-muted="true"] {
    color: var(--text-secondary);
    opacity: 0.6;
  }
  &[data-muted="true"]:hover {
    opacity: 0.85;
  }
`;

// `data-no-pill` opts out of the global button-pill rule (global.css) so the 8px from linkStyles applies, matching the link rows.
function SidebarButton({
  muted,
  ...props
}: ComponentProps<typeof SidebarButtonElement> & { muted?: boolean }) {
  return <SidebarButtonElement {...props} data-no-pill="" data-muted={muted} />;
}

const Badge = styled("span")`
  position: relative;
  background: var(--alpha4);
  color: var(--text-secondary);
  font-size: 9px;
  font-weight: 500;
  line-height: 12px;
  display: inline-flex;
  min-width: 16px;
  width: fit-content;
  border-radius: 10px;
  padding: 2px 4px;
  justify-content: center;
  margin-left: auto;

  &[data-hidden="true"] {
    opacity: 0;
  }
`;

const BadgeDotContainer = styled("div")`
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--item-bg);
`;

type PatternInput = NonNullable<Parameters<typeof useRouteMatch>[0]>;

type ItemProps<T extends PatternInput> = {
  scope: T;
  destination: string;
  children: ReactNode;
};

function Item<T extends PatternInput>({
  scope,
  destination,
  children,
}: ItemProps<T>) {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const [match] = useRouteMatch(scope);
  const href = makeUrl(scope);

  return (
    <StyledLink
      to={href}
      onClick={() => recordClick("sidebar", destination)}
      style={
        match
          ? ({
              backgroundColor: "var(--alpha4)",
              "--item-bg": "var(--item-active-bg)",
            } as React.CSSProperties)
          : { backgroundColor: "transparent" }
      }
    >
      {children ?? ""}
    </StyledLink>
  );
}

function PersonalItem({ space }: { space: Row["spaces"] | undefined }) {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const [match] = useRouteMatch(routes.personal);
  const [hover, setHover] = useState(false);
  const href = makeUrl(routes.personal);

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink
        to={href}
        onClick={() => recordClick("sidebar", "personal")}
        style={{
          backgroundColor: match || hover ? "var(--alpha4)" : "transparent",
          flex: 1,
          paddingRight: 32,
          overflow: "hidden",
        }}
      >
        <Icon name="circle-user-round" size={16} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis>Personal</Text>
        </span>
      </StyledLink>
      {space && (
        <SpaceAction>
          <SpaceActionsMenu space={space} />
        </SpaceAction>
      )}
    </SpaceItemContainer>
  );
}

function DiscoverableSpaceItem({ space }: { space: Row["spaces"] }) {
  const [modalOpen, setModalOpen] = useState(false);
  const displayName = useSpaceDisplayName();
  const recordClick = useRecordNavigationClick();

  return (
    <SpaceItemContainer>
      <SidebarButton
        muted
        onPress={() => {
          recordClick("sidebar", "discoverable_space");
          setModalOpen(true);
        }}
      >
        <Tooltip content="Discoverable space, click to join">
          <DiscoverableSpaceIcon />
        </Tooltip>
        <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Text ellipsis>{displayName(space)}</Text>
        </span>
      </SidebarButton>
      <JoinSpaceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        space={space}
      />
    </SpaceItemContainer>
  );
}

function SpaceItem({
  space,
  viewOnly,
}: {
  space: Row["spaces"];
  viewOnly?: boolean;
}) {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const [match] = useRouteMatch(routes.space, { spaceId: space.id });
  const [hover, setHover] = useState(false);
  const href = makeUrl(routes.space, { spaceId: space.id });
  const displayName = useSpaceDisplayName();

  const icon = <Icon name={spaceIcon(space.mode)} size={16} />;
  const iconTooltip = spaceIconTooltip(space.mode);

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink
        to={href}
        onClick={() => recordClick("sidebar", "space")}
        style={{
          backgroundColor: match || hover ? "var(--alpha4)" : "transparent",
          flex: 1,
          paddingRight: 32,
          overflow: "hidden",
          ...(viewOnly && {
            color: "var(--text-secondary)",
            opacity: hover ? 0.85 : 0.6,
          }),
        }}
      >
        {iconTooltip ? <Tooltip content={iconTooltip}>{icon}</Tooltip> : icon}
        <span style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis>{displayName(space)}</Text>
        </span>
      </StyledLink>
      {viewOnly ? (
        <div
          style={{
            position: "absolute",
            right: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
          }}
        >
          <Tooltip content="Viewing as admin, not a member">
            <span
              style={{ display: "inline-flex", color: "var(--text-secondary)" }}
            >
              <Icon name="eye" size={12} />
            </span>
          </Tooltip>
        </div>
      ) : (
        <SpaceAction>
          <SpaceActionsMenu space={space} />
        </SpaceAction>
      )}
    </SpaceItemContainer>
  );
}

export function Navigation() {
  const subjectUserId = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const adminLanding = useAdminLandingView();
  if (subjectUserId || adminSpace || adminLanding) {
    return <AdminNavigation subjectUserId={subjectUserId} />;
  }
  return <WorkspaceNavigation />;
}

function WorkspaceNavigation() {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const navEnter = useNavTransition("workspace");
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  const [discoverableCollapsed, setDiscoverableCollapsed] =
    useDiscoverableSpacesCollapsed();
  const personalWorkEnabled = usePersonalWorkEnabled();
  const [nonPersonalSpaces] = useQuery(
    queries.spacesLite({ modes: ["public", "private"] })
  );
  const memberSpaces = nonPersonalSpaces.filter(isSpaceMember);
  const discoverableSpaces = nonPersonalSpaces.filter((s) => !isSpaceMember(s));
  const [personalSpace] = useQuery(
    personalWorkEnabled ? queries.personalSpace() : undefined
  );
  const personalSpaceId = personalSpace?.id ?? null;
  const canCreateSpace = useHasAccess("tenant:space_create");

  const [, spaceRouteParams] = useRouteMatch(routes.space);
  const activeSpaceId = (spaceRouteParams as { spaceId?: string }).spaceId;
  const memberSpaceIds = new Set(memberSpaces.map((s) => s.id));
  const [activeSpaceResult] = useQuery(
    queries.navSpace({
      spaceId: activeSpaceId ?? "",
    })
  );
  const activeSpace =
    activeSpaceId &&
    !memberSpaceIds.has(activeSpaceId) &&
    activeSpaceId !== personalSpaceId
      ? activeSpaceResult
      : null;
  const pendingRequestCount = usePendingRequestCount();

  return (
    <Container ref={scrollRef} data-nav-enter={navEnter}>
      <Item scope={routes.recents} destination="recents">
        <Icon name="history" size={16} />
        <Text>Recents</Text>
      </Item>
      <Item scope={routes.favorites} destination="favorites">
        <Icon name="heart" size={16} />
        <Text>Favorites</Text>
      </Item>
      <Break />
      <Item scope={routes.monitoring} destination="monitoring">
        <Icon name="scan-heart" size={16} />
        <Text>Monitoring</Text>
        {pendingRequestCount > 0 && (
          <Badge>
            {pendingRequestCount}
            <BadgeDotContainer>
              <BadgeDot />
            </BadgeDotContainer>
          </Badge>
        )}
      </Item>
      <Item scope={routes.links} destination="links">
        <Icon name="product-cases" size={16} />
        <Text>Links</Text>
      </Item>
      <Item scope={routes.connectors} destination="connectors">
        <Icon name="api-key-custom" size={16} />
        <Text>Connectors</Text>
      </Item>
      <Item scope={routes.skills} destination="skills">
        <Icon name="skills" size={16} />
        <Text>Skills</Text>
      </Item>
      <Break />
      <Heading>
        <Text variant="text-small">Spaces</Text>
        <div style={{ display: "flex", gap: 2 }}>
          <Tooltip
            content={
              !canCreateSpace
                ? "You don't have permission to create spaces"
                : "Create space"
            }
          >
            {canCreateSpace ? (
              <SubtleButtonLink
                icon="plus"
                variant="secondary"
                to={makeUrl(routes.modal.createSpace)}
                onClick={() => recordClick("sidebar", "create_space")}
              />
            ) : (
              <SubtleButton icon="plus" variant="secondary" disabled />
            )}
          </Tooltip>
        </div>
      </Heading>

      {personalWorkEnabled ? (
        <PersonalItem space={personalSpace} />
      ) : (
        <SpaceItemContainer>
          <Tooltip content="Personal work is disabled" fill="stretch">
            <DisabledPersonalItem role="link" aria-disabled="true">
              <Icon name="circle-user-round" size={16} />
              <Text>Personal</Text>
            </DisabledPersonalItem>
          </Tooltip>
        </SpaceItemContainer>
      )}

      {[...memberSpaces]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((space) => (
          <SpaceItem key={space.id} space={space} />
        ))}
      {activeSpace && (
        <SpaceItem key={activeSpace.id} space={activeSpace} viewOnly />
      )}
      {discoverableSpaces.length > 0 && (
        <>
          <Heading>
            <Text variant="text-small">Discoverable</Text>
            <div style={{ display: "flex", gap: 2 }}>
              <SubtleButton
                icon="chevron-right"
                variant="secondary"
                aria-label={
                  discoverableCollapsed
                    ? "Expand discoverable spaces"
                    : "Collapse discoverable spaces"
                }
                onClick={() => setDiscoverableCollapsed(!discoverableCollapsed)}
                // Rotating the icon-only 24×24 square in place keeps it pixel-identical to the Spaces `+` button while the centered chevron turns; the `icon` prop can't be styled directly.
                style={{
                  transition: "transform var(--motion-fast) var(--ease-out)",
                  transform: discoverableCollapsed
                    ? undefined
                    : "rotate(90deg)",
                }}
              />
            </div>
          </Heading>
          <ExpandWrapper data-expanded={!discoverableCollapsed}>
            <CollapseInner inert={discoverableCollapsed || undefined}>
              {[...discoverableSpaces]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((space) => (
                  <DiscoverableSpaceItem key={space.id} space={space} />
                ))}
            </CollapseInner>
          </ExpandWrapper>
        </>
      )}
    </Container>
  );
}
